/**
 * Leaderboard — ranked prompts and contributors, medals for the top three.
 *
 * The API ranks the week from the `downloads` log and falls back to all-time when the week is
 * empty; the note under the segmented control reports whichever period actually came back, so
 * the screen never claims a weekly ranking it did not compute.
 */

import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchLeaderboard } from '../api/community';
import { Icon } from '../components/Icon';
import { Media } from '../components/Media';
import { Padded, Screen } from '../components/Screen';
import { ErrorState, Placeholder, SkeletonCells } from '../components/feedback';
import { Avatar, SegmentedControl } from '../components/primitives';
import { SourceStatLine } from '../components/SourceStats';
import { compactNumber, decimal1 } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { haptic } from '../hooks/useTg';

type Tab = 'prompts' | 'contributors';
type Period = 'week' | 'all';

const MEDALS = ['🥇', '🥈', '🥉'];

export function Leaderboard() {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [tab, setTab] = useState<Tab>('prompts');
  const [period, setPeriod] = useState<Period>('week');

  const query = useQuery({
    /**
     * The period is part of the key, not just part of the request.
     *
     * `fetchLeaderboard` has accepted `'week' | 'all'` since it was written and this screen
     * passed a hardcoded `'week'`, so the all-time board the API computes was unreachable — and
     * they are genuinely different: measured live, the two answers differ from the first row
     * down. Adding the control without widening the key would have been worse than leaving it
     * out, because both periods would then share one cache entry and the screen would show
     * whichever arrived first under whichever label was selected.
     */
    queryKey: ['leaderboard', period],
    queryFn: () => fetchLeaderboard(period),
    staleTime: 5 * 60_000,
  });

  const go = useCallback(
    (path: string) => {
      haptic('light');
      navigate(path);
    },
    [navigate],
  );

  const board = query.data;
  const rows = tab === 'prompts' ? (board?.prompts ?? []) : (board?.contributors ?? []);

  return (
    <Screen chrome="surface">
      <div className="hairline-b sticky top-0 z-10 bg-surface px-gutter pb-3 pt-2.5">
        <SegmentedControl<Tab>
          value={tab}
          onChange={setTab}
          options={[
            { value: 'prompts', label: t.lbPrompts },
            { value: 'contributors', label: t.lbContributors },
          ]}
        />
        <div className="flex items-center justify-between gap-3 pt-2.5">
          {/*
            The note still reports whichever period actually came back, not the one selected.
            The API falls back to all-time when the week is empty, and a board labelled "this
            week" that is silently all-time would be the screen lying about its own contents.
          */}
          {board ? (
            <p className="min-w-0 flex-1 truncate text-footnote text-hint">
              {board.period === 'week'
                ? t.lbNoteWeek(compactNumber(board.memberCount, lang))
                : t.lbNoteAll(compactNumber(board.memberCount, lang))}
            </p>
          ) : (
            <span className="flex-1" />
          )}
          <div className="flex-none">
            <SegmentedControl<Period>
              size="sm"
              value={period}
              onChange={setPeriod}
              options={[
                { value: 'week', label: t.lbPeriodWeek },
                { value: 'all', label: t.lbPeriodAll },
              ]}
            />
          </div>
        </div>
      </div>

      {query.isLoading ? (
        <Padded className="pt-3">
          <SkeletonCells count={6} />
        </Padded>
      ) : query.isError ? (
        <ErrorState
          message={(query.error as Error)?.message}
          onRetry={() => void query.refetch()}
        />
      ) : rows.length === 0 ? (
        <Placeholder icon="trophy" title={t.lbEmpty} description={t.noResultsSub} />
      ) : (
        <Padded className="flex flex-col gap-2 pt-3">
          {tab === 'prompts'
            ? board!.prompts.map((entry) => (
                <button
                  key={entry.prompt.id}
                  type="button"
                  onClick={() => go(`/prompt/${entry.prompt.id}`)}
                  className="flex items-center gap-2.5 rounded-card bg-surface p-2.5 text-start transition-transform duration-fast ease-pv active:scale-[0.985]"
                >
                  <Rank rank={entry.rank} lang={lang} />
                  <span className="h-11 w-11 flex-none overflow-hidden rounded-thumb">
                    <Media
                      seed={entry.prompt.id}
                      example={entry.prompt.firstExample}
                      ratio="1 / 1"
                      className="h-full w-full"
                    />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="bidi-plaintext block truncate text-callout font-semibold text-text">
                      {entry.prompt.title}
                    </span>
                    {/*
                      The source's numbers, not ours.

                      This row read "★ 0.0" beside a download icon showing the *ranking score* —
                      so a prompt with 586 real likes on youmind displayed as "0.0 · 30 downloads",
                      two numbers that are both untrue. Nobody has rated anything and the 30 was an
                      internal score wearing a download icon. Same normaliser as the card and the
                      bot: one number, and it says where it came from.
                    */}
                    <SourceStatLine engagement={entry.prompt.sourceEngagement} />
                  </span>
                </button>
              ))
            : board!.contributors.map((entry) => (
                <button
                  key={entry.author.key}
                  type="button"
                  onClick={() => go(`/author/${encodeURIComponent(entry.author.key)}`)}
                  className="flex items-center gap-2.5 rounded-card bg-surface p-2.5 text-start transition-transform duration-fast ease-pv active:scale-[0.985]"
                >
                  <Rank rank={entry.rank} lang={lang} />
                  <Avatar
                    name={entry.author.name}
                    photoUrl={entry.author.photoUrl}
                    seed={entry.author.key}
                    size={44}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="bidi-plaintext block truncate text-callout font-semibold text-text">
                      {entry.author.name}
                    </span>
                    <span className="tnum flex items-center gap-1.5 truncate text-footnote text-hint">
                      <span className="truncate">
                        {t.promptsCount(compactNumber(entry.promptCount, lang))}
                      </span>
                      <span aria-hidden="true" className="opacity-45">
                        ·
                      </span>
                      <span className="truncate">{t.leaderboardCreatorScore}</span>
                    </span>
                  </span>
                  {/*
                    The score is an average percentile within the creator's own source, so it is
                    rendered as a plain number rather than borrowing the download icon. A unit-free
                    rank dressed as a download count is a lie about what was measured.
                  */}
                  <span className="tnum flex-none text-footnote font-semibold text-accent">
                    {compactNumber(entry.score, lang)}
                  </span>
                </button>
              ))}
        </Padded>
      )}

      <div className="h-6" />
    </Screen>
  );
}

function Rank({ rank, lang }: { rank: number; lang: 'en' | 'fa' }) {
  const medal = MEDALS[rank - 1];
  return (
    <span
      // `rank` localised, and no ASCII `#`: the numeral is the content, and a Farsi reader
      // should hear ۳ rather than "#3".
      aria-label={compactNumber(rank, lang)}
      className={`tnum w-6 flex-none text-center font-semibold text-hint ${
        medal ? 'text-title' : 'text-callout'
      }`}
    >
      {medal ?? compactNumber(rank, lang)}
    </span>
  );
}
